
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import { cloneDeep, isEqual } from "lodash-es";

  import LIST_USER_POSTINGS from "@/apollo/jobboard/ListUserPostings.gql";

  import DefaultContentLayout from "@/components/scaffold/DefaultContentLayout.vue";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import CardPagination from "@/components/card/PaginationNew.vue";
  import PostingCard from "@/components/jobboard/PostingCardV2.vue";
  import PostingFilters from "@/components/recruitings/PostingFiltersV2.vue";

  import { getFiltersDataFromQuery, joinQueryValues } from "@/helpers/router/prepareFiltersQuery";

  import contractorPostingsFiltersTemplate from "@/data/filtersTemplates/contractorPostingsFiltersTemplate";
  import paginationConfig from "@/data/paginationConfig";

  import {
    Company,
    Posting,
    Query_RootPostingArgs,
    User,
    Date_Comparison_Exp,
  } from "@/generated/graphql";
  import Roles from "@/entities/Roles";
  import PaginationConfig from "@/entities/PaginationConfig";
  import ContractorPostingsFiltersQuery from "@/entities/filters/ContractorPostingsFiltersQuery";
  import { getPaginationDataFromQuery, getPaginationQuery } from "@/helpers/router/preparePaginationQuery";
  import { getEmptyFiltersResultMessage, getEmptyListMessage } from "@/helpers/lists/emptyListMessages";
  import EmptyListImagePosting from "@/components/icons/emptyListImages/EmptyListImagePosting.vue";
  import getStateQueryConditions from "@/helpers/locations/stateQueryConditions";
  import ActionPanel from "@/components/common/ActionsPanel.vue";

  @Component({
    components: {
      EmptyListImagePosting,
      DefaultContentLayout,
      ActionPanel,
      EmptyListPlaceholder,
      PostingFilters,
      CardPagination,
      LoadingOverlay,
      PostingCard,
    },
    apollo: {
      postings: {
        query: LIST_USER_POSTINGS,
        variables() {
          return this.postingFilter;
        },
        update(data) {
          this.paginationConfig.total = data.total.aggregate.count;
          this.paginationConfig.loadedCount = data.posting.length;
          return data.posting;
        },
      },
    },
  })
  export default class Postings extends Vue {
    @Prop({type: Object, required: true}) readonly currentUser!: User;
    @Prop({type: Array, required: true}) readonly companies!: Company[];
    @Prop({type: Boolean, required: true}) readonly isMarketplace!: boolean;

    postings: Posting[] | null = null;

    paginationConfig: PaginationConfig = { ...paginationConfig };

    postingFilters: ContractorPostingsFiltersQuery = cloneDeep(contractorPostingsFiltersTemplate);

    get isLoading(): boolean {
      return this.$apollo.queries.postings.loading;
    }

    get emptyListMessage(): string {
      const entitiesName = "job postings";
      const filtersEmpty = isEqual(this.postingFilters, contractorPostingsFiltersTemplate);

      if (filtersEmpty) {
        return getEmptyListMessage(entitiesName);
      }

      return getEmptyFiltersResultMessage(entitiesName);
    }

    get empty(): boolean {
      if(!this.postings) {
        return false;
      }

      return this.postings?.length === 0;
    }

    get isManager(): boolean {
      return this.currentUser.role === Roles.MANAGER;
    }

    get postingFilter(): Query_RootPostingArgs {
      const { page, limit } = this.paginationConfig;
      const nextOffset = page * limit - limit;

      const {
        countries,
        states,
        cities,
        industries,
        companies,
        minDuration,
        maxDuration,
        jobTitle,
        skills,
      } = this.postingFilters;

      const filterConditions = [];

      if (this.isMarketplace) {
        filterConditions.push({
          public: { _eq: true },
        });
      }

      if (!this.isMarketplace) {
        filterConditions.push({
          company: {
            contractors: {
              user_id: { _eq: this.currentUser.id },
              deleted: { _eq: false },
              approved: { _eq: true },
            },
          },
        });
      }

      if (this.companies?.length && !this.isMarketplace) {
        filterConditions.push({
          company_id: {
            _in: this.companies.map(({id}) => id)
          }
        });
      }

      if (jobTitle) {
        filterConditions.push({
          job_title: { _ilike: `%${jobTitle}%`},
        });
      }

      if (countries.length) {
        filterConditions.push({
          country: { _in: countries },
        });
      }

      if (states.length) {
        filterConditions.push({
          state: getStateQueryConditions({countries, states}),
        });
      }

      if (cities.length) {
        filterConditions.push({
          city: { _in: cities },
        });
      }

      if (companies.length && !this.isMarketplace) {
        filterConditions.push({
          company: { business_name: { _in: companies } },
        });
      }

      if (skills.length) {
        filterConditions.push({
          skills: {
            skill_id: {
              _in: skills
            },
          },
        });
      }

      if (industries.length) {
        filterConditions.push({
          industries: {
            industry: {
              name: { _in: industries },
            },
          },
        });
      }

      if (minDuration || maxDuration) {
        const duration: Date_Comparison_Exp = {};

        if (maxDuration) {
          duration._lte = maxDuration;
        }

        if (minDuration) {
          duration._gte = minDuration;
        }

        filterConditions.push({ duration });
      }

      return {
        offset: nextOffset,
        limit: limit,
        order_by: [{created_at: 'desc'}] as any,
        where: { _and: [...filterConditions, {
             active: {
               _eq: true,
             },
          }],
        },
      };
    }

    get queryPropsToWatch(): string {
      return JSON.stringify([
        this.postingFilters,
        this.paginationConfig
      ]);
    }

    get queryParams(): {filters: ContractorPostingsFiltersQuery, pagination: PaginationConfig} {
      const query = this.$route.query;
      const {
        job_title: jobTitle,
        min_duration: minDuration,
        max_duration: maxDuration,
        ...queryParams
      } = query;

      const queryData = {
        jobTitle,
        minDuration,
        maxDuration,
        ...queryParams,
      };

      const filters = getFiltersDataFromQuery<ContractorPostingsFiltersQuery>(contractorPostingsFiltersTemplate, queryData);
      const pagination = {...this.paginationConfig, ...getPaginationDataFromQuery(query)};

      return {
        filters,
        pagination,
      };
    }

    get isChosenPageEmpty(): boolean {
      const notFirstPage = this.paginationConfig.page !== 1;

      return !this.isLoading && this.empty && notFirstPage;
    }

    @Watch("queryPropsToWatch")
    updateQuery(): void {
      if (this.isChosenPageEmpty) {
        return this.showFirstPage();
      }

      const paginationQuery = getPaginationQuery(this.paginationConfig);

      const {
        jobTitle,
        minDuration,
        maxDuration,
        ...filtersData
      } = this.postingFilters;

      const query = joinQueryValues({
        ...paginationQuery,
        ...filtersData,
        job_title: jobTitle,
        min_duration: minDuration,
        max_duration: maxDuration,
      });

      if (isEqual(query, this.$route.query)) {
        return;
      }

      this.$router.push({query}).catch(()=>{});
    }

    @Watch('isMarketplace')
    tabChanged(): void {
      this.postings = null;
      this.postingFilters = cloneDeep(contractorPostingsFiltersTemplate);
      this.paginationConfig = { ...paginationConfig };
    }

    @Watch("$route.query", { immediate: true })
    onQueryChange(): void {
      const { filters, pagination } = this.queryParams;

      this.postingFilters = filters;
      this.paginationConfig = pagination;
    }

    showFirstPage(): void {
      this.paginationConfig = {...this.paginationConfig, page: paginationConfig.page };
    }

    updateFilters(updatedFilters: ContractorPostingsFiltersQuery): void {
      const {
        industries,
        countries,
        states,
        cities,
        skills,
        companies,
        jobTitle,
        minDuration,
        maxDuration,
      } = updatedFilters;

      const dataEqual = isEqual(this.postingFilters, {
        industries,
        countries,
        states,
        cities,
        skills,
        companies,
        jobTitle,
        minDuration,
        maxDuration,
      });

      if (dataEqual) {
        return;
      }

      this.postingFilters = updatedFilters;
    }

    resetFilters(): void {
      if (isEqual(this.postingFilters, contractorPostingsFiltersTemplate)) {
        return;
      }

      this.postingFilters = cloneDeep(contractorPostingsFiltersTemplate);
      this.paginationConfig = { ...paginationConfig };
    }
  }
